import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageService } from 'primeng/api';
import { TemplateService } from '../../../services/template.service';
import { ProjectFormService } from '../../../services/projectform.service';
import { TemplateModel } from '../../../models/template.model';
import { TEMPLATE_TYPE } from '../../../models/template.constants';

@Component({
  selector: 'app-import-form-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule
  ],
  templateUrl: './import-form-modal.html',
  styleUrl: './import-form-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportFormModalComponent implements OnInit {
  projectId!: number;
  templates: TemplateModel[] = [];
  filteredTemplates: TemplateModel[] = [];
  selectedTemplates: TemplateModel[] = [];
  searchText: string = '';
  loading: boolean = false;
  selectAll: boolean = false;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private templateService: TemplateService,
    private projectFormService: ProjectFormService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.projectId = this.config.data?.projectId;
  }

  ngOnInit(): void {
    this.loadFormTemplates();
  }

  loadFormTemplates(): void {
    this.loading = true;
    // Load Form templates (type_id = 2)
    this.templateService.getTemplateList(TEMPLATE_TYPE.FORMS).subscribe({
      next: (templates) => {
        this.templates = templates.filter(t => t.isActive);
        this.filteredTemplates = [...this.templates];
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading form templates:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load form templates'
        });
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onSearch(): void {
    if (!this.searchText.trim()) {
      this.filteredTemplates = [...this.templates];
    } else {
      const search = this.searchText.toLowerCase();
      this.filteredTemplates = this.templates.filter(t =>
        t.templateName?.toLowerCase().includes(search)
      );
    }
    this.selectAll = false;
    this.selectedTemplates = [];
    this.cdr.markForCheck();
  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      this.selectedTemplates = [...this.filteredTemplates];
    } else {
      this.selectedTemplates = [];
    }
    this.cdr.markForCheck();
  }

  import(): void {
    if (this.selectedTemplates.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select at least one template to import'
      });
      return;
    }

    this.loading = true;
    const templateIds = this.selectedTemplates
      .map(t => t.templateId)
      .filter((id): id is number => id !== null && id !== undefined);

    const request = {
      projectId: this.projectId,
      templateIds: templateIds,
      updatedBy: 1 // TODO: Get from current user context
    };

    this.projectFormService.importFormTemplates(request).subscribe({
      next: (folders) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${folders.length} form template(s) imported successfully`
        });
        this.ref.close(folders);
      },
      error: (error) => {
        console.error('Error importing form templates:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to import form templates'
        });
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  cancel(): void {
    this.ref.close();
  }
}
